import Sortable from 'sortablejs';
import ApplicationController from './application_controller';

// Header designer canvas — the second custom-form editor tab. Unlike the Form Designer (which
// arranges controls owned by the jQuery editor singleton), header components carry no
// user-entered datum, so this controller owns its cells outright. The canvas has TWO placement zones — header and footer — separated by a static
// "document body" placeholder that stands in for the form's rendered output. The palette adds
// cells to the header zone; cells can be dragged between zones (shared SortableJS group). Each
// cell serializes {id, slug, x, y, w, properties} into the hidden textarea saved as
// custom_form_template[header]: header cells under grid.cells, footer cells under footer.cells.
// Grid mechanics (reorder, drag-to-resize grip, x/y from width accumulation)
// mirror form_designer_controller so the designers feel identical.
export default class extends ApplicationController {
  static targets = [
    'canvas',
    'footerCanvas',
    'hidden',
    'emptyNotice',
    'footerEmptyNotice',
    'donorPicker',
    'loadConfirmBtn',
    'settingsName',
    'settingsPrintToggle',
    'undoButton',
    'redoButton',
    'tableStyleToggle',
    'palette',
  ];

  // Sortable group names: the two zones swap cells with each other and accept drops cloned out of
  // the palette, which never accepts anything and never reorders.
  static CELLS_GROUP = 'header-canvas-cells';

  static PALETTE_GROUP = 'header-palette';

  static values = {
    header: Object,
    // The standard Ambiki header as a design (CustomFormTemplate.standard_header_design): what
    // the canvas opens with when the template has no design, and what Reset puts back.
    standardDesign: Object,
    minWidths: Object,
    defaultMinWidth: Number,
    slugNames: Object,
  };

  // Components that default to hidden-on-print when placed. MSS has never printed anywhere in
  // the app, so a placed MSS keeps that behavior unless the designer explicitly turns it on.
  static HIDE_ON_PRINT_BY_DEFAULT = ['header-mss'];

  // Ceiling for a single pasted image inside a Rich Text Block, matching the visual Rich Text
  // Block's MAX_IMAGE_BYTES in shared/form/editor_controls/visual/_text_block.html.erb. The
  // CKEditor build ships Base64UploadAdapter, so a paste lands in the design JSON as a data:
  // URI — and unlike a body control, a header design is copied onto form_data.header for EVERY
  // document created from the template and inlined into the load-design picker's data attribute.
  // An unbounded paste therefore multiplies across the whole document history, not one record.
  static MAX_IMAGE_BYTES = 80 * 1024;

  // Undo / redo keep at most this many steps.
  static HISTORY_LIMIT = 50;

  connect() {
    const { header, footer, tableStyle } = this.initialCells();
    this.headerCells = header;
    this.footerCells = footer;
    this.tableStyle = tableStyle;
    this.sortables = [];
    this.editors = [];
    this.undoStack = [];
    this.redoStack = [];
    this.lastHistoryKey = null;
    this.boundOnKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.boundOnKeydown);
    this.render();
    this.updateHistoryButtons();
    this.initPaletteSortables();
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundOnKeydown);
    this.destroySortables();
    (this.paletteSortables || []).forEach((sortable) => sortable.destroy());
    this.paletteSortables = [];
    this.destroyEditors();
  }

  // The palette is a drag source: a button dragged out is cloned (the palette keeps its button),
  // and while it hovers over a zone it shows as the box it will become (see the
  // .layout-cell-drop-preview styles). Disabled buttons stay put.
  initPaletteSortables() {
    this.paletteSortables = this.paletteTargets.map(
      (list) =>
        new Sortable(list, {
          group: { name: this.constructor.PALETTE_GROUP, pull: 'clone', put: false },
          sort: false,
          draggable: '.btn-header-editor',
          filter: '.disabled, [disabled]',
          preventOnFilter: false,
          ghostClass: 'layout-cell-drop-preview',
          animation: 150,
        })
    );
  }

  // --- Undo / redo: a memento of both zones, taken BEFORE every change. ---

  snapshotState() {
    return JSON.stringify({ header: this.headerCells, footer: this.footerCells, tableStyle: this.tableStyle });
  }

  // Record the current state as the step to undo back to. `key` coalesces a burst of edits to
  // the same thing (typing in a text block) into one step: while consecutive changes carry the
  // same key, only the first records. A no-op change (the state already on top of the stack) is
  // not recorded either, so undo never appears to do nothing.
  recordHistory(key = null) {
    if (key && key === this.lastHistoryKey) return;
    const snapshot = this.snapshotState();
    this.lastHistoryKey = key;
    if (this.undoStack.length && this.undoStack[this.undoStack.length - 1] === snapshot) return;
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.constructor.HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack = [];
    this.updateHistoryButtons();
  }

  applySnapshot(snapshot) {
    const state = JSON.parse(snapshot);
    this.headerCells = state.header || [];
    this.footerCells = state.footer || [];
    this.tableStyle = !!state.tableStyle;
    this.lastHistoryKey = null;
    this.render();
  }

  undo() {
    if (this.undoStack.length === 0) return;
    this.redoStack.push(this.snapshotState());
    this.applySnapshot(this.undoStack.pop());
    this.updateHistoryButtons();
  }

  redo() {
    if (this.redoStack.length === 0) return;
    this.undoStack.push(this.snapshotState());
    this.applySnapshot(this.redoStack.pop());
    this.updateHistoryButtons();
  }

  updateHistoryButtons() {
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = this.undoStack.length === 0;
    if (this.hasRedoButtonTarget) this.redoButtonTarget.disabled = this.redoStack.length === 0;
  }

  // Ctrl/Cmd+Z undoes and Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y redoes, but only while this tab is the
  // one showing, and never while the keystroke belongs to something else: a field, the Rich Text
  // Block's CKEditor (which has its own undo), or an open modal.
  onKeydown(event) {
    if (!isHistoryShortcut(event)) return;
    if (isEditableTarget(event.target) || document.body.classList.contains('modal-open')) return;
    if (!this.element.classList.contains('active')) return;
    event.preventDefault();
    if (isRedoShortcut(event)) this.redo();
    else this.undo();
  }

  destroySortables() {
    (this.sortables || []).forEach((s) => s.destroy());
    this.sortables = [];
  }

  destroyEditors() {
    (this.editors || []).forEach((editor) => {
      try {
        editor.destroy();
      } catch {
        // Editor was already torn down with its DOM node — nothing to release.
      }
    });
    this.editors = [];
  }

  // A template with no design opens showing the standard header as components — the default
  // header exists on the canvas to be rearranged, trimmed or added to, not as an invisible
  // fallback. (Blank means "no design", so an emptied-and-saved design comes back this way too.)
  initialCells() {
    const saved = this.parseDesign(this.headerValue);
    if (saved.header.length === 0 && saved.footer.length === 0) return this.standardCells();
    return saved;
  }

  hasStandardDesign() {
    return !!(this.standardDesignValue && this.standardDesignValue.grid);
  }

  standardCells() {
    return this.hasStandardDesign()
      ? this.parseDesign(this.standardDesignValue)
      : { header: [], footer: [], tableStyle: false };
  }

  // Does the current canvas still match the standard header exactly, table style included?
  // Compared by what a cell IS (slug, width, properties) in order — ids are not identity, and
  // x/y follow from the widths.
  matchesStandardDesign() {
    if (!this.hasStandardDesign() || this.footerCells.length > 0) return false;
    const standard = this.standardCells();
    if (!!this.tableStyle !== !!standard.tableStyle) return false;
    const key = (cells) => JSON.stringify(cells.map((cell) => [cell.slug, Number(cell.w) || 4, cell.properties || {}]));
    return key(this.headerCells) === key(standard.header);
  }

  // Normalize a saved design JSON ({style, grid: {cells}, footer: {cells}}) into the two zone
  // arrays plus the table-style flag ("Table style": lay the output out like the standard
  // header's tables rather than as label-over-value blocks).
  parseDesign(value) {
    const parseZone = (raw) => {
      const list = Array.isArray(raw) ? raw : Object.values(raw || {});
      return list
        .filter((cell) => cell && cell.slug && cell.id)
        .map((cell) => ({
          id: cell.id,
          slug: cell.slug,
          w: Number(cell.w) || 4,
          properties: cell.properties || {},
        }));
    };
    const design = value || {};
    return {
      header: parseZone(design.grid && design.grid.cells),
      footer: parseZone(design.footer && design.footer.cells),
      tableStyle: design.style === 'table',
    };
  }

  // The "Table style" toggle button in the card header.
  toggleTableStyle() {
    this.recordHistory();
    this.tableStyle = !this.tableStyle;
    this.syncTableStyleToggle();
    this.serialize();
  }

  // Paint the toggle's pressed state: aria-pressed for assistive tech, Bootstrap's .active for
  // the pressed look, and a switch icon that reads on/off at a glance.
  syncTableStyleToggle() {
    if (!this.hasTableStyleToggleTarget) return;
    const on = !!this.tableStyle;
    const button = this.tableStyleToggleTarget;
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.classList.toggle('active', on);
    const icon = button.querySelector('.bi');
    if (icon) {
      icon.classList.toggle('bi-toggle-on', on);
      icon.classList.toggle('bi-toggle-off', !on);
    }
  }

  // "Load design from another template" modal — the Impulse autocomplete's commit event hands
  // us the selected option element, which carries the donor design JSON in a data attribute.
  donorSelected(event) {
    const { target } = event.detail;
    try {
      this.selectedDonorDesign = target && target.dataset.header ? JSON.parse(target.dataset.header) : null;
    } catch {
      this.selectedDonorDesign = null;
    }
    if (this.hasLoadConfirmBtnTarget) this.loadConfirmBtnTarget.disabled = !this.selectedDonorDesign;
  }

  donorCleared() {
    this.selectedDonorDesign = null;
    if (this.hasLoadConfirmBtnTarget) this.loadConfirmBtnTarget.disabled = true;
  }

  // Copy-on-load: cells get fresh IDs so the copy is independent of the donor template — later
  // edits to the donor do not propagate, matching the system's snapshot philosophy. The picker
  // clears itself afterward: a lingering "selected" template would wrongly imply a live link.
  confirmLoad() {
    if (!this.selectedDonorDesign) return;
    this.recordHistory();
    const { header, footer, tableStyle } = this.parseDesign(this.selectedDonorDesign);
    this.headerCells = header.map((cell) => ({ ...cell, id: this.generateId() }));
    this.footerCells = footer.map((cell) => ({ ...cell, id: this.generateId() }));
    this.tableStyle = tableStyle;
    this.render();
    if (this.hasDonorPickerTarget && typeof this.donorPickerTarget.clear === 'function') {
      this.donorPickerTarget.clear(); // visually empties the picker input
    }
    // awc-autocomplete's clear() API does NOT emit awc-autocomplete:clear (only the clear
    // BUTTON does), so reset our state explicitly — otherwise the Load button stays enabled
    // with an empty picker and a click would destructively re-apply the stale donor design.
    this.donorCleared();
  }

  // "Reset to standard": put the standard header's components back, in their usual arrangement,
  // with no footer.
  reset() {
    if (this.matchesStandardDesign()) return; // already the standard header — nothing to do or undo
    this.recordHistory();
    const { header, footer, tableStyle } = this.standardCells();
    this.headerCells = header;
    this.footerCells = footer;
    this.tableStyle = tableStyle;
    this.render();
  }

  allCells() {
    return [...this.headerCells, ...this.footerCells];
  }

  minWidthFor(slug) {
    const map = this.minWidthsValue || {};
    return map[slug] || this.defaultMinWidthValue || 2;
  }

  friendlyName(slug) {
    return (this.slugNamesValue && this.slugNamesValue[slug]) || slug;
  }

  cellById(id) {
    return this.allCells().find((cell) => cell.id === id);
  }

  generateId() {
    const pad = (n, len) => String(n).padStart(len, '0');
    return `header_${pad(Math.floor(Math.random() * 1000000), 6)}_${pad(Math.floor(Math.random() * 100000), 5)}`;
  }

  // Palette button click — add a component to the HEADER zone (drag it down to the footer zone
  // if that's where it belongs). Every component may be placed any number of times, in either
  // zone: they are read-only displays of patient/practice facts, so copies cannot conflict.
  addComponent(event) {
    const btn = event.currentTarget;
    const { slug } = btn.dataset;
    if (!slug) return;
    this.recordHistory();
    this.headerCells.push(this.buildNewCell(slug));
    this.render();
  }

  // A fresh cell for a palette component: default width, the print default, and an empty text
  // property for the plain Text Block.
  buildNewCell(slug) {
    const minW = this.minWidthFor(slug);
    const hideOnPrint = this.constructor.HIDE_ON_PRINT_BY_DEFAULT.includes(slug) ? '1' : '0';
    const cell = {
      id: this.generateId(),
      slug,
      w: Math.max(minW, 4),
      properties: { hide_on_print: { value: hideOnPrint } },
    };
    if (slug === 'header-text-block') cell.properties.text = { value: '' };
    return cell;
  }

  // Something arrived in a zone. A cell dragged across from the other zone is handled by
  // re-reading both zones from the DOM. A palette button dropped in (SortableJS has put the
  // button itself where it was dropped; its clone stays in the palette) is taken back out and
  // becomes a new cell at that position in the zone it landed in.
  onZoneAdd(event, zone) {
    const item = event && event.item;
    if (!item || !item.matches('.btn-header-editor')) {
      this.syncFromDom();
      return;
    }
    const { slug } = item.dataset;
    const cells = zone === this.footerCanvasTarget ? this.footerCells : this.headerCells;
    const index = Number.isInteger(event.newIndex) ? Math.min(event.newIndex, cells.length) : cells.length;
    item.remove();
    if (!slug) return;
    this.recordHistory();
    cells.splice(index, 0, this.buildNewCell(slug));
    this.render();
  }

  removeCell(event) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const { cellId } = col.dataset;
    this.recordHistory();
    this.headerCells = this.headerCells.filter((cell) => cell.id !== cellId);
    this.footerCells = this.footerCells.filter((cell) => cell.id !== cellId);
    this.render();
  }

  // Per-component "hide on print" toggle. Stored as a wrapped property ({value: '0'|'1'}) to
  // match the structure-tab property schema, so a future back-port to body components is additive.
  // The "Hide when printing" checkbox in the settings modal. Applies immediately to the cell
  // the modal was opened for.
  togglePrint(event) {
    const cell = this.cellById(this.settingsCellId);
    if (!cell) return;
    this.recordHistory();
    cell.properties = cell.properties || {};
    const hidden = event.currentTarget.checked;
    cell.properties.hide_on_print = { value: hidden ? '1' : '0' };
    const col = this.element.querySelector(`.layout-cell[data-cell-id="${this.settingsCellId}"]`);
    if (col) this.paintPrintFlag(col, hidden);
    this.serialize();
  }

  // The gear button: opens the shared per-component settings modal, synced to the cell that
  // asked for it. Settings apply immediately.
  openSettings(event) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const cell = this.cellById(col.dataset.cellId);
    if (!cell) return;
    this.settingsCellId = cell.id;
    if (this.hasSettingsNameTarget) this.settingsNameTarget.textContent = this.friendlyName(cell.slug);
    if (this.hasSettingsPrintToggleTarget) {
      this.settingsPrintToggleTarget.checked = !!(
        cell.properties &&
        cell.properties.hide_on_print &&
        cell.properties.hide_on_print.value === '1'
      );
    }
    if (window.$) $('#headerComponentSettingsModal').modal('show');
  }

  textChanged(event) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const cell = this.cellById(col.dataset.cellId);
    if (!cell) return;
    this.recordHistory(`text:${cell.id}`); // one undo step per typing burst
    cell.properties = cell.properties || {};
    cell.properties.text = { value: event.currentTarget.value };
    this.serialize();
  }

  render() {
    this.destroySortables();
    this.destroyEditors();
    this.renderZone(this.canvasTarget, this.headerCells);
    this.renderZone(this.footerCanvasTarget, this.footerCells);
    this.toggleNotices();
    this.syncTableStyleToggle();

    // One shared group so cells drag freely between the header and footer zones. The static
    // document-body placeholder sits between the zones in the markup and is not a Sortable
    // container, so nothing can be dropped "into" the body.
    [this.canvasTarget, this.footerCanvasTarget].forEach((zone) => {
      this.sortables.push(
        new Sortable(zone, {
          group: {
            name: this.constructor.CELLS_GROUP,
            pull: true,
            put: [this.constructor.CELLS_GROUP, this.constructor.PALETTE_GROUP],
          },
          animation: 150,
          // The whole toolbar strip drags; the gear and the appended remove segment live
          // inside it, so they are filtered out of drag starts but keep working as clicks.
          handle: '.layout-cell-toolbar',
          filter: '.btn, .layout-cell-remove',
          preventOnFilter: false,
          draggable: '.layout-cell',
          ghostClass: 'layout-cell-ghost',
          onEnd: () => this.syncFromDom(),
          onAdd: (event) => this.onZoneAdd(event, zone),
        })
      );
    });

    this.initRichTextEditors();
    this.serialize();
  }

  // Rich Text Block cells host a BalloonEditor (same CKEditor build the Form Designer and fill
  // pages use — /CKEditor5/ckeditor.js is in the page head). Initialized after the cells are in
  // the DOM so the balloon toolbar can position itself; falls back to a plain contenteditable
  // box if the CKEditor global is somehow missing.
  initRichTextEditors() {
    this.element.querySelectorAll('.header-rich-text-editor').forEach((el) => {
      const col = el.closest('.layout-cell');
      const cell = col && this.cellById(col.dataset.cellId);
      if (!cell) return;
      const currentValue = (cell.properties && cell.properties.text && cell.properties.text.value) || '';
      const store = (html) => {
        this.recordHistory(`text:${cell.id}`); // one undo step per typing burst
        cell.properties = cell.properties || {};
        cell.properties.text = { value: html };
        this.serialize();
      };

      // Degraded fallback (CKEditor unavailable): edit the value as raw source TEXT, never
      // innerHTML — the stored value is designer-authored HTML that only the show page's
      // sanitize() scrubs, and innerHTML here would execute event-handler payloads
      // (<img onerror=...>) inside the editor. textContent round-trips losslessly.
      const fallbackEditor = () => {
        el.setAttribute('contenteditable', 'true');
        el.textContent = currentValue;
        el.addEventListener('input', () => store(el.textContent));
      };

      if (!window.BalloonEditor) {
        fallbackEditor();
        return;
      }

      window.BalloonEditor.create(el, {
        licenseKey: window.ckeditorConfigLicenseKey,
        placeholder: 'Your text…',
        // As at every other editor: the "mathematical" text transformations are off, so a header
        // keeps "3/4" and "->" as typed (shared/form/controls/_body.html.erb says why).
        typing: { transformations: { remove: ['mathematical'] } },
      })
        .then((editor) => {
          editor.setData(currentValue);
          this.watchEditorData(editor, currentValue, store);
          this.editors.push(editor);
        })
        .catch(() => fallbackEditor());
    });
  }

  // Mirrors the visual Rich Text Block's change:data handler: every edit is stored, but a pasted
  // image larger than MAX_IMAGE_BYTES is re-encoded down before it reaches the design JSON. If it
  // still won't fit we tell the designer and roll the editor back to the last accepted state
  // rather than silently storing a multi-megabyte data: URI.
  //
  // `processing` guards the re-entrant change:data that our own setData() fires, and
  // `lastAccepted` is the rollback target (also the fast path for a no-op change).
  watchEditorData(editor, initialValue, store) {
    let processing = false;
    let lastAccepted = initialValue;

    editor.model.document.on('change:data', async () => {
      if (processing) return;
      const data = editor.getData();
      if (data === lastAccepted) return;

      const oversized = findOversizedDataUris(data, this.constructor.MAX_IMAGE_BYTES);
      if (oversized.length === 0) {
        lastAccepted = data;
        store(data);
        return;
      }

      processing = true;
      try {
        let next = data;
        for (const src of oversized) {
          // eslint-disable-next-line no-await-in-loop
          const compressed = await compressBase64Image(src, this.constructor.MAX_IMAGE_BYTES);
          if (!compressed) {
            // Matches the visual Rich Text Block's own too-large-image alert: the paste must be
            // refused before the editor rolls back, and the canvas has no inline error surface.
            // eslint-disable-next-line no-alert
            window.alert(
              'Error: That image is too large, even after compression.\n' +
                'Please use a smaller image, or use the Practice Logo component instead.'
            );
            editor.setData(lastAccepted);
            return;
          }
          next = next.replace(src, compressed);
        }
        lastAccepted = next;
        editor.setData(next);
        store(next);
      } finally {
        processing = false;
      }
    });
  }

  renderZone(zone, cells) {
    zone.querySelectorAll('.layout-cell').forEach((el) => el.remove());
    zone.classList.add('row', 'layout-canvas-row', 'header-canvas-zone');
    cells.forEach((cell) => {
      const minW = this.minWidthFor(cell.slug);
      cell.w = clamp(Number(cell.w) || 4, minW, 12);
      zone.appendChild(this.buildCell(cell, minW));
    });
  }

  // Rebuild both zone arrays from the DOM after a drag — covers reorders within a zone and
  // moves between zones (SortableJS physically relocates the element). Then queue a full
  // re-render: re-parenting a cell that hosts a live CKEditor breaks the editor's view
  // bindings, so rebuild cleanly. Deferred a frame (and de-duped — onEnd and onAdd both fire on
  // a cross-zone move) so Sortable finishes its own callback before its containers are wiped.
  syncFromDom() {
    const byId = {};
    this.allCells().forEach((cell) => {
      byId[cell.id] = cell;
    });
    const readZone = (zone) =>
      Array.from(zone.querySelectorAll('.layout-cell'))
        .map((col) => byId[col.dataset.cellId])
        .filter(Boolean);
    const header = readZone(this.canvasTarget);
    const footer = readZone(this.footerCanvasTarget);
    // onEnd and onAdd both land here on a cross-zone move, and a drag dropped back in place
    // changes nothing: only record (and re-sync) when the order really moved.
    const ids = (cells) => cells.map((cell) => cell.id).join(',');
    if (ids(header) !== ids(this.headerCells) || ids(footer) !== ids(this.footerCells)) {
      this.recordHistory();
      this.headerCells = header;
      this.footerCells = footer;
    }
    this.serialize();
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this.render();
    });
  }

  toggleNotices() {
    if (this.hasEmptyNoticeTarget) this.emptyNoticeTarget.classList.toggle('hidden', this.headerCells.length > 0);
    if (this.hasFooterEmptyNoticeTarget)
      this.footerEmptyNoticeTarget.classList.toggle('hidden', this.footerCells.length > 0);
  }

  buildCell(cell, minW) {
    const col = document.createElement('div');
    col.className = `col-12 col-md-${cell.w} layout-cell`;
    col.dataset.cellId = cell.id;
    col.dataset.slug = cell.slug;
    col.dataset.minW = String(minW);
    col.dataset.w = String(cell.w);

    const hideOnPrint = cell.properties && cell.properties.hide_on_print && cell.properties.hide_on_print.value === '1';
    const defaultMin = this.defaultMinWidthValue || 2;
    const minHint =
      minW > defaultMin
        ? `<span class="layout-width-min" title="${escapeHtml(cell.slug)} cannot shrink below ${minW}/12">min ${minW}</span>`
        : '';
    col.innerHTML = `
      <div class="layout-cell-inner layout-cell-header">
        <div class="layout-cell-edit-view">
          <div class="layout-cell-toolbar" title="Drag to reorder or move between header and footer">
            <span class="layout-cell-tools">
              <i class="bi bi-grip-vertical layout-drag-handle"></i>
              <button type="button" class="btn layout-cell-settings" data-action="header-canvas#openSettings" title="Component settings"><i class="bi bi-gear"></i></button>
            </span>
            <span class="layout-cell-slug" title="${escapeHtml(cell.slug)}">${escapeHtml(this.friendlyName(cell.slug))}</span>
            ${minHint}
            <span class="header-print-flag" title="Hidden on printed/PDF output" hidden><i class="bi bi-eye-slash"></i></span>
            <span class="layout-width-value" title="Width (drag the corner grip to resize)">${cell.w}/12</span>
            <button type="button" class="layout-cell-remove" data-action="header-canvas#removeCell" title="Remove"><i class="bi bi-x-lg"></i></button>
          </div>
          <div class="layout-cell-label"></div>
        </div>
        <div class="layout-cell-resize-grip" data-action="mousedown->header-canvas#startResize" title="Drag to resize"></div>
      </div>
    `;

    const body = col.querySelector('.layout-cell-label');
    if (cell.slug === 'header-text-block') {
      const textarea = document.createElement('textarea');
      textarea.className = 'form-control form-control-sm autoresize';
      textarea.rows = 2;
      textarea.placeholder = 'Your text…';
      textarea.value = (cell.properties && cell.properties.text && cell.properties.text.value) || '';
      textarea.dataset.action = 'input->header-canvas#textChanged';
      body.appendChild(textarea);
    } else if (cell.slug === 'header-rich-text-block') {
      const editorEl = document.createElement('div');
      editorEl.className = 'header-rich-text-editor';
      body.appendChild(editorEl);
    } else {
      const label = document.createElement('div');
      label.className = 'layout-cell-label-text';
      label.textContent = this.friendlyName(cell.slug);
      body.appendChild(label);
    }

    this.paintPrintFlag(col, hideOnPrint);
    return col;
  }

  // Print-toggle title changes with state, delivered through the app's Bootstrap tooltip system
  // (data-toggle="tooltip"), not the bare title attribute. Bootstrap caches the text in
  // data-original-title, so state changes update that and refresh any open tooltip.
  paintPrintFlag(col, hidden) {
    const flag = col.querySelector('.header-print-flag');
    if (flag) flag.hidden = !hidden;
  }

  // --- Width mechanics: identical UX to form_designer_controller ---

  startResize(event) {
    event.preventDefault();
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const rowEl = col.parentElement;
    if (!rowEl) return;
    const unitWidth = rowEl.getBoundingClientRect().width / 12;
    if (!unitWidth || !Number.isFinite(unitWidth)) return;
    const startX = event.clientX;
    const startW = Number(col.dataset.w) || 4;
    const minW = Number(col.dataset.minW) || 2;
    const colRect = col.getBoundingClientRect();

    const ghost = document.createElement('div');
    ghost.className = 'layout-cell-resize-ghost';
    ghost.style.top = `${colRect.top}px`;
    ghost.style.left = `${colRect.left}px`;
    ghost.style.height = `${colRect.height}px`;
    ghost.style.width = `${colRect.width}px`;
    const label = document.createElement('span');
    label.className = 'layout-cell-resize-ghost-label';
    label.textContent = `${startW} / 12`;
    ghost.appendChild(label);
    document.body.appendChild(ghost);

    let targetW = startW;

    const onMove = (e) => {
      const dw = Math.round((e.clientX - startX) / unitWidth);
      const nextW = clamp(startW + dw, minW, 12);
      targetW = nextW;
      ghost.style.width = `${nextW * unitWidth}px`;
      label.textContent = `${nextW} / 12`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('layout-cell-resizing');
      ghost.remove();
      if (targetW !== startW) {
        this.setCellWidth(col, targetW);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.classList.add('layout-cell-resizing');
  }

  setCellWidth(col, nextW) {
    const minW = Number(col.dataset.minW) || 2;
    const w = clamp(nextW, minW, 12);
    if (w === Number(col.dataset.w)) return;
    this.recordHistory();
    Array.from(col.classList).forEach((cls) => {
      if (/^col-md-\d+$/.test(cls)) col.classList.remove(cls);
    });
    col.classList.add(`col-md-${w}`);
    col.dataset.w = String(w);
    const cell = this.cellById(col.dataset.cellId);
    if (cell) cell.w = w;
    const valueEl = col.querySelector('.layout-width-value');
    if (valueEl) valueEl.textContent = `${w}/12`;
    this.serialize();
  }

  // Same x/y-from-width-accumulation walk as form_designer_controller#serialize, run per zone.
  // Header cells land in grid.cells, footer cells in footer.cells. Both zones empty serializes
  // to {} — blank means "no design": standard header, no footer.
  //
  // A canvas that still matches the standard header exactly also serializes to {}: it IS the
  // standard header, and blank keeps documents from templates nobody redesigned rendering the
  // standard header unchanged. Change anything and the real design is stored.
  serialize() {
    if ((this.headerCells.length === 0 && this.footerCells.length === 0) || this.matchesStandardDesign()) {
      this.hiddenTarget.value = '{}';
      return;
    }
    const out = { grid: { columns: 12, cells: serializeZone(this.headerCells) } };
    if (this.tableStyle) out.style = 'table';
    if (this.footerCells.length > 0) {
      out.footer = { columns: 12, cells: serializeZone(this.footerCells) };
    }
    this.hiddenTarget.value = JSON.stringify(out);
  }
}

function serializeZone(cells) {
  const out = [];
  let x = 0;
  let y = 0;
  cells.forEach((cell) => {
    const w = Number(cell.w) || 4;
    if (x + w > 12) {
      y += 1;
      x = 0;
    }
    out.push({ id: cell.id, slug: cell.slug, x, y, w, properties: cell.properties || {} });
    x += w;
    if (x >= 12) {
      y += 1;
      x = 0;
    }
  });
  return out;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y.
export function isHistoryShortcut(event) {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return false;
  const key = String(event.key || '').toLowerCase();
  return key === 'z' || key === 'y';
}

export function isRedoShortcut(event) {
  const key = String(event.key || '').toLowerCase();
  return key === 'y' || (key === 'z' && event.shiftKey);
}

// Keystrokes inside a field or an editor belong to it (native undo, CKEditor's undo), never to
// the canvas.
export function isEditableTarget(target) {
  return !!(
    target &&
    typeof target.closest === 'function' &&
    target.closest('input, textarea, select, [contenteditable="true"], .ck-editor__editable')
  );
}

// Decoded byte length of a base64 payload, without decoding it. Every 4 base64 characters carry
// 3 bytes; trailing '=' padding characters each stand in for one absent byte.
export function base64ByteSize(base64) {
  const clean = String(base64 || '').replace(/=+$/, '');
  return Math.floor((clean.length * 3) / 4);
}

// Every data: image URI in the HTML whose decoded payload exceeds maxBytes, de-duplicated so the
// same image pasted twice is only re-encoded once (String#replace then rewrites both copies).
export function findOversizedDataUris(html, maxBytes) {
  const matches = String(html || '').match(/data:image\/[^;]+;base64,[^"')\s]+/g) || [];
  return [...new Set(matches)].filter((src) => base64ByteSize(src.split(',')[1]) > maxBytes);
}

// Re-encode a data: URI as progressively lower-quality JPEG until it fits, or give up. Same
// quality ladder as the visual Rich Text Block so both editors degrade images identically.
export function compressBase64Image(src, maxBytes) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      for (const quality of [0.7, 0.6, 0.5, 0.4]) {
        const jpeg = canvas.toDataURL('image/jpeg', quality);
        if (base64ByteSize(jpeg.split(',')[1]) <= maxBytes) {
          resolve(jpeg);
          return;
        }
      }
      resolve(null);
    };
    // A data: URI that the browser cannot decode never fires onload; treat it as incompressible
    // rather than leaving the designer's editor wedged behind the `processing` guard.
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
